import "../plan.scss"

import {
  Banner,
  Card,
  Divider,
  Icon,
  ProgressBar,
  Text,
} from "@shopify/polaris"
import {
  ChevronRightIcon,
  DeliveryIcon,
  PaymentIcon,
} from "@shopify/polaris-icons"
import { observer } from "mobx-react"
import { focusInstance } from "polaris-enhance"
import { useEffect, useMemo, useState } from "react"

import APIApp from "@/api/app"
import { FormatPriceText } from "@/components"
import { PLAN_LIST_MAP } from "@/constant"
import stores from "@/stores"
import dayjs from "@/utils/dayjs"
import { formatDisplayNumber } from "@/utils/formatNumber"
import { hasChosenPlan } from "@/utils/plan"

const getTrialTimeText = (restTrialDays: number, restSeconds: number) => {
  if (restTrialDays > 1) {
    return `${restTrialDays} days`
  }
  const hours = Math.max(1, Math.ceil(restSeconds / 3600))
  return `${hours} hours`
}

const DottedInfoRow = (props: { label: string; value: React.ReactNode }) => {
  const { label, value } = props
  return (
    <div className="flex items-center gap-3 h-5 sm:whitespace-nowrap">
      <Text variant="bodyMd" as="span" tone="subdued">
        {label}
      </Text>
      <svg
        className="opacity-0 sm:opacity-100 flex-1"
        width="976"
        height="2"
        viewBox="0 0 976 2"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M0 1H976"
          stroke="#E3E3E3"
          strokeWidth="2"
          strokeDasharray="6 6"
        />
      </svg>

      <Text variant="bodyMd" as="span">
        {value}
      </Text>
    </div>
  )
}

const CarrierBillingRow = (props: {
  icon: typeof PaymentIcon
  title: string
  badge: React.ReactNode
  onClick: () => void
}) => {
  const { icon, title, badge, onClick } = props
  return (
    <div
      className="flex items-center gap-2 px-3 py-[10px] cursor-pointer
        hover:bg-[var(--p-color-bg-surface-hover)]
        active:bg-[var(--p-color-bg-surface-active)]"
      onClick={onClick}
    >
      <div className="shrink-0">
        <Icon source={icon} tone="base" />
      </div>
      <div className="flex-1 min-w-0 space-y-1 sm:space-y-0 sm:flex sm:flex-row sm:items-center sm:justify-between sm:gap-2">
        <Text variant="bodyMd" as="span">
          {title}
        </Text>
        <span className="w-fit max-w-full bg-[rgba(0,0,0,0.06)] rounded-lg px-2 py-[2px] break-words">
          <Text variant="bodySm" as="span" fontWeight="medium" tone="subdued">
            {badge}
          </Text>
        </span>
      </div>
      <div className="shrink-0 self-center">
        <Icon source={ChevronRightIcon} tone="base" />
      </div>
    </div>
  )
}

const AccountInfoNew = () => {
  const { PlanStore, UserInfoStore } = stores
  const { completedCurPlan, config } = PlanStore
  const userPkg = config?.user_pkg

  const {
    plan_total_quota = 0,
    plan_used_quota = 0,
    cur_period_end,
    extra_charge_count,
    package_period,
    package_kind = 0,
    total_ins_price,
    rest_trial_days = 0,
    rest_seconds = 0,
    label_fee = 0,
  } = userPkg || {}

  const [balance, setBalance] = useState(UserInfoStore.data.balance.toString())

  useEffect(() => {
    APIApp.userBaseInfo().then(({ data, code }) => {
      if (code !== 1000 || !data) return
      setBalance(data.balance)
      UserInfoStore.setBalance(Number(data.balance))
    })
  }, [UserInfoStore])

  const hasSelectedPlan = hasChosenPlan()
  const showTrialBanner = rest_trial_days > 0 || rest_seconds > 0
  const trialTimeText = getTrialTimeText(rest_trial_days, rest_seconds)

  const packageName = PLAN_LIST_MAP()[package_kind]?.planText ?? t("Free")
  const { origin_price, actual_price } = completedCurPlan || {}

  const planSummaryText = useMemo(() => {
    if (!hasSelectedPlan) return ""
    if (package_kind === 1) {
      return t("Free")
    }
    if (userPkg?.source === 3) {
      const price = userPkg.shopify_origin_price ?? 0
      const periodLabel =
        package_period === 1 ? t("Month").toLowerCase() : t("Annually")
      return `ParcelWILL Bundle - $${formatDisplayNumber(price)}/${periodLabel}`
    }
    const price = origin_price ?? actual_price ?? 0
    if (package_period === 1) {
      return `${packageName} - $${formatDisplayNumber(price)}/${t("Month").toLowerCase()}`
    }
    return `${packageName} - $${formatDisplayNumber(price)}/${t("Annually")}`
  }, [
    hasSelectedPlan,
    package_kind,
    packageName,
    package_period,
    origin_price,
    actual_price,
    userPkg?.source,
    userPkg?.shopify_origin_price,
  ])

  const quotaProgress =
    plan_total_quota > 0
      ? (100 / plan_total_quota) * (plan_total_quota - plan_used_quota)
      : 0

  const resetDateText = cur_period_end
    ? dayjs.unix(cur_period_end).format("DD MMM YYYY")
    : t("Free")

  const showReturnProtectionSales =
    hasSelectedPlan &&
    package_period === 1 &&
    package_kind > 1 &&
    Number(total_ins_price) > 0

  return (
    <div className="rounded-2xl border border-[#e3e3e3] bg-white p-4 space-y-3">
      <div className="space-y-1">
        <Text variant="headingSm" as="h2">
          {t("PlanBilling")}
        </Text>

        {hasSelectedPlan ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-4">
              <Text variant="bodyMd" as="span">
                {planSummaryText}
              </Text>
              <Text variant="bodyMd" as="span">
                {formatDisplayNumber(plan_total_quota - plan_used_quota)} /{" "}
                {formatDisplayNumber(plan_total_quota)}
              </Text>
            </div>
            <div className="space-y-1">
              <div className="plan-progressbar-height">
                <ProgressBar
                  progress={quotaProgress}
                  size="small"
                  tone="primary"
                />
              </div>
              {package_period === 1 && package_kind > 1 ? (
                <Text variant="bodyMd" as="p" tone="subdued">
                  {t("AdditionalQuotaUsed")}:{" "}
                  <Text variant="bodyMd" as="span" fontWeight="medium">
                    {formatDisplayNumber(extra_charge_count)}
                  </Text>
                </Text>
              ) : (
                <Text variant="bodyMd" as="p" tone="subdued">
                  {t("AdditionalQuotaNotApplicable")}
                </Text>
              )}
            </div>
          </div>
        ) : (
          <Text variant="bodyMd" as="p">
            {t("NoPlanSelectedYet")}
          </Text>
        )}

        {hasSelectedPlan && (
          <div className="space-y-1">
            <DottedInfoRow
              label={t("NextQuotaResetDate")}
              value={resetDateText}
            />
            {showReturnProtectionSales && (
              <DottedInfoRow
                label={t("FreeReturnServiceSales")}
                value={
                  <FormatPriceText
                    value={total_ins_price ?? 0}
                    currency={UserInfoStore.data.currency_format}
                  />
                }
              />
            )}
          </div>
        )}

        {showTrialBanner && (
          <div className="pp-remove-banner-shadow !mt-3">
            <Card padding={"0"}>
              <Banner tone="info">
                <p>
                  {hasSelectedPlan
                    ? t("FreeTrialEndsSwitchPlanNoCost", {
                        replace: { time: trialTimeText },
                      })
                    : t("FreeTrialEndsPlanRequired", {
                        replace: { time: trialTimeText },
                      })}
                </p>
              </Banner>
            </Card>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Text variant="headingSm" as="h2">
          {t("ParcelwillCarrierBilling")}
        </Text>
        <div className="rounded-lg border border-[#e3e3e3] border-solid overflow-hidden">
          <CarrierBillingRow
            icon={PaymentIcon}
            title={t("AddFunds")}
            badge={
              <>
                {t("RemainingBalance")}:{" "}
                <FormatPriceText value={balance} currency="$" />
              </>
            }
            onClick={() => pnav("/settings/add-funds")}
          />
          <div className="px-4">
            <Divider />
          </div>
          <CarrierBillingRow
            icon={DeliveryIcon}
            title={t("ManageCarrierBilling")}
            badge={
              <>
                {t("LabelSpent")}:{" "}
                <FormatPriceText value={label_fee} currency="$" />
              </>
            }
            onClick={() => {
              pnav("/settings/logistics-integration")
              focusInstance.add("carriers-payment")
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default observer(AccountInfoNew)
